/**
 * 017-style structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/roles-permissions/users-table/columns.tsx —
 * same plain `ColumnDef[]` shape as `../customers/columns.tsx`, no `createColumnHelper`.
 *
 * Only the data, columns and labels are ours. No column re-derives anything: `outcome` and
 * `eventType` are rendered exactly as the API sent them (constitution I).
 */
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { getStatusBadge } from "@steelyard/theme";
import { eventTypeLabel, type AuditLogEntry, type ServiceAuditEntry } from "../../lib/services/audit";
import { formatMoment } from "../../lib/format";
import { userPath } from "../../lib/paths";

/**
 * The account's name as the service sent it, linked to their user record. The id stays in the link
 * only — a name the server could not resolve (an erased account) reads "Unknown user", never the id.
 * Linked by the user number where the entry carries one; otherwise by the id URL, which redirects.
 */
function AccountName({ value, name, number }: { value: string | null; name: string | null; number?: string | null }) {
  if (!value) return <span className="text-gray-500">—</span>;
  return (
    <Link to={userPath(number || value)} className="text-primary hover:underline">
      {name ?? "Unknown user"}
    </Link>
  );
}

export const auditColumns: ColumnDef<AuditLogEntry>[] = [
  {
    id: "occurredAt",
    accessorKey: "occurredAt",
    header: "When",
    size: 190,
    cell: ({ row }) => <span className="whitespace-nowrap text-gray-600">{formatMoment(row.original.occurredAt)}</span>,
  },
  {
    id: "eventType",
    accessorKey: "eventType",
    header: "Event",
    size: 220,
    cell: ({ row }) => <span className="font-medium">{eventTypeLabel(row.original.eventType)}</span>,
  },
  {
    id: "outcome",
    accessorKey: "outcome",
    header: "Outcome",
    size: 140,
    cell: ({ row }) => getStatusBadge(row.original.outcome),
  },
  {
    id: "subject",
    accessorFn: (entry) => entry.subjectName ?? "",
    header: "Subject account",
    size: 150,
    cell: ({ row }) => <AccountName value={row.original.subjectAccountId} name={row.original.subjectName} number={row.original.subjectNumber} />,
  },
  {
    id: "actor",
    accessorFn: (entry) => entry.actorName ?? "",
    header: "Actor",
    size: 150,
    cell: ({ row }) =>
      row.original.actorAccountId ? (
        <AccountName value={row.original.actorAccountId} name={row.original.actorName} number={row.original.actorNumber} />
      ) : (
        <span className="text-xs text-gray-500 italic">unauthenticated</span>
      ),
  },
  {
    id: "sourceIp",
    accessorKey: "sourceIp",
    header: "Source IP",
    size: 130,
    cell: ({ row }) => <span className="font-mono text-xs text-gray-600">{row.original.sourceIp ?? "—"}</span>,
  },
  {
    id: "client",
    accessorKey: "client",
    header: "Client",
    size: 110,
    cell: ({ row }) => row.original.client ?? <span className="text-gray-500">—</span>,
  },
  {
    id: "change",
    header: "Change",
    size: 90,
    enableSorting: false,
    // On changes only (a role change, a matrix change) — `before`/`after` are otherwise both null.
    cell: ({ row }) => {
      const { before, after } = row.original;
      if (!before && !after) return <span className="text-gray-500">—</span>;
      return (
        <details className="text-xs">
          <summary className="cursor-pointer text-primary select-none">View</summary>
          <div className="mt-2 max-w-xs space-y-1">
            {before ? <pre className="overflow-x-auto rounded bg-gray-50 p-2 whitespace-pre-wrap">{before}</pre> : null}
            {after ? <pre className="overflow-x-auto rounded bg-gray-50 p-2 whitespace-pre-wrap">{after}</pre> : null}
          </div>
        </details>
      );
    },
  },
];

/** One row shape for every service's own audit log (`listServiceAudit`) — rendered as sent. */
export const serviceAuditColumns: ColumnDef<ServiceAuditEntry>[] = [
  {
    id: "occurredAt",
    accessorKey: "occurredAt",
    header: "When",
    size: 190,
    cell: ({ row }) => <span className="whitespace-nowrap text-gray-600">{formatMoment(row.original.occurredAt)}</span>,
  },
  {
    id: "action",
    accessorKey: "action",
    header: "Action",
    size: 200,
    cell: ({ row }) => <span className="font-medium">{row.original.action.replaceAll("_", " ")}</span>,
  },
  {
    id: "entity",
    accessorFn: (entry) => `${entry.entityType ?? ""} ${entry.entityLabel ?? ""}`,
    header: "Record",
    size: 200,
    cell: ({ row }) =>
      row.original.entityType || row.original.entityId ? (
        <span className="text-xs">
          {row.original.entityType ?? "record"}{" "}
          {row.original.entityId ? <span className="text-gray-600">{row.original.entityLabel ?? "—"}</span> : null}
        </span>
      ) : (
        <span className="text-gray-500">—</span>
      ),
  },
  {
    id: "summary",
    accessorKey: "summary",
    header: "What changed",
    size: 320,
    cell: ({ row }) => row.original.summary ?? <span className="text-gray-500">—</span>,
  },
  {
    id: "actor",
    accessorFn: (entry) => entry.actorName ?? "",
    header: "Actor",
    size: 150,
    cell: ({ row }) =>
      row.original.actorId ? (
        <AccountName value={row.original.actorId} name={row.original.actorName} />
      ) : (
        <span className="text-xs text-gray-500 italic">system</span>
      ),
  },
];
